import { Bell } from "lucide-react";
import { useGetIdentity, useGetList, useUpdate } from "ra-core";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface Notification {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  link: string | null;
  read_at: string | null;
  created_at: string;
}

const timeAgo = (iso: string) => {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
  return new Date(iso).toLocaleDateString();
};

/**
 * The notification bell, on real rows.
 *
 * RLS already limits `notifications` to the signed-in recipient, so this does
 * no filtering of its own, the list a user can read IS their list. Marking as
 * read happens on click rather than on open: opening the menu to glance at
 * what arrived should not silently clear it.
 */
export const NotificationsMenu = () => {
  const { identity } = useGetIdentity();
  const [update] = useUpdate();

  const { data: notifications, refetch } = useGetList<Notification>(
    "notifications",
    {
      pagination: { page: 1, perPage: 20 },
      sort: { field: "created_at", order: "DESC" },
    },
    { enabled: Boolean(identity?.id) },
  );

  const items = notifications ?? [];
  const unread = items.filter((item) => !item.read_at);

  const markRead = (notification: Notification) => {
    if (notification.read_at) return;
    update(
      "notifications",
      {
        id: notification.id,
        data: { read_at: new Date().toISOString() },
        previousData: notification,
      },
      { mutationMode: "pessimistic", onSuccess: () => refetch() },
    );
  };

  const markAllRead = () => {
    const now = new Date().toISOString();
    unread.forEach((notification) =>
      update(
        "notifications",
        {
          id: notification.id,
          data: { read_at: now },
          previousData: notification,
        },
        { mutationMode: "pessimistic" },
      ),
    );
    setTimeout(() => refetch(), 300);
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative size-8 rounded-md text-muted-foreground hover:text-foreground"
          aria-label={
            unread.length
              ? `Notifications, ${unread.length} unread`
              : "Notifications"
          }
        >
          <Bell className="size-4" />
          {unread.length ? (
            <span className="absolute right-1 top-1 size-2 rounded-full bg-destructive" />
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 rounded-lg p-0">
        <div className="flex items-center justify-between px-2 pt-2">
          <DropdownMenuLabel className="text-[13px]">
            Notifications
          </DropdownMenuLabel>
          {unread.length ? (
            <button
              className="px-2 text-[12px] text-muted-foreground hover:text-foreground"
              onClick={markAllRead}
            >
              Mark all read
            </button>
          ) : null}
        </div>

        {items.length === 0 ? (
          <p className="px-3 pb-3 pt-1 text-[12px] text-muted-foreground">
            You're all caught up.
          </p>
        ) : (
          <ul className="max-h-80 overflow-y-auto pb-1">
            {items.map((notification) => {
              const content = (
                <div
                  className={cn(
                    "min-w-0 border-b border-border-subtle px-3 py-2 last:border-0",
                    !notification.read_at && "bg-accent/40",
                  )}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-[13px] font-medium text-foreground">
                      {notification.title}
                    </span>
                    <span className="shrink-0 text-[11px] text-muted-foreground">
                      {timeAgo(notification.created_at)}
                    </span>
                  </div>
                  {notification.body ? (
                    <p className="line-clamp-2 text-[12px] leading-4 text-muted-foreground">
                      {notification.body}
                    </p>
                  ) : null}
                </div>
              );
              return (
                <li key={notification.id}>
                  {notification.link ? (
                    <Link
                      to={notification.link}
                      className="block no-underline"
                      onClick={() => markRead(notification)}
                    >
                      {content}
                    </Link>
                  ) : (
                    <button
                      className="block w-full text-left"
                      onClick={() => markRead(notification)}
                    >
                      {content}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
